import {
  Box,
  BoxProps,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  PaperTypeMap,
  Typography,
  useTheme
} from "@mui/material";
import React, { DispatchWithoutAction, useState } from "react";
import { IControl, ILoaded, IMutator, IOptions } from "sonobello.utilities.react";
import { Nullable } from "sonobello.utilities.react.mui";

import { EnhancedAppointment } from "../../../../../Models/Appointment";
import { DateTime } from "../../../../../Types/DateTime";

export const classNames = {
  placeholder: "appointmentPickerPlaceholder",
  card: "appointmentPickerCard",
  spinner: "appointmentPickerSpinner"
};

interface IAppointmentCardProps extends Partial<PaperTypeMap>, IControl<EnhancedAppointment> {
  /** A flag indicating that this card represents the currently selected appointment for the picker. */
  selected: boolean;
  /** The action to be executed when the card is clicked on by the user. */
  onClick: DispatchWithoutAction;
}

const AppointmentCard: React.FC<IAppointmentCardProps> = ({ selected, value, onClick }) => {
  const theme = useTheme();
  return (
    <Card
      className={classNames.card}
      onClick={onClick}
      sx={{
        boxSizing: "border-box",
        height: "100%",
        p: 0.5,
        outline: selected ? `3px solid ${theme.palette.primary.main}` : undefined,
        outlineOffset: "-3px",
        fontWeight: selected ? "bold" : undefined
      }}
    >
      <CardHeader
        title={`${value.startTime.toLocaleString(DateTime.DATE_MED)} | ${value.startTime.toLocaleString(
          DateTime.TIME_SIMPLE
        )} - ${value.endTime.toLocaleString(DateTime.TIME_SIMPLE)}`}
        titleTypographyProps={{ variant: "body1" }}
        sx={{ p: 0.5, color: theme.palette.primary.main }}
      />
      <CardContent
        sx={{
          padding: `${theme.spacing(0)} ${theme.spacing(1)} ${theme.spacing(0)} ${theme.spacing(1)} !important`
        }}
      >
        {value.services.length ? (
          <>
            <Typography variant="body2">{`Services: ${value.services.map(s => s.name).join(", ")}`}</Typography>
            <Typography variant="body2">{`Employees: ${value.services
              .map(s => s.employeeName)
              .join(", ")}`}</Typography>
          </>
        ) : (
          <Typography variant="body2">No Services</Typography>
        )}
      </CardContent>
    </Card>
  );
};

export type IAppointmentPickerProps = IMutator<EnhancedAppointment> &
  IOptions<EnhancedAppointment> &
  ILoaded &
  Omit<BoxProps, "onChange">;

const AppointmentPicker: React.FC<IAppointmentPickerProps> = ({ isLoading, options, onChange, ...rest }) => {
  const [value, setValue] = useState<Nullable<EnhancedAppointment>>(null);

  const handleClick = (appointment: EnhancedAppointment) => {
    setValue(appointment);
    onChange(appointment);
  };

  return (
    <Box display="flex" flexDirection="column" p={2} sx={{ overflowY: "auto" }} {...rest}>
      {isLoading ? (
        <Box display="flex" alignItems="center" justifyContent="center" flexGrow={1}>
          <CircularProgress className={classNames.spinner} />
        </Box>
      ) : options.length ? (
        <Grid container spacing={1} justifyContent="space-around">
          {options.map(appointment => (
            <Grid item key={appointment.id}>
              <AppointmentCard
                value={appointment}
                selected={value?.id === appointment.id}
                onClick={() => handleClick(appointment)}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography className={classNames.placeholder} variant="body2" align="center">
          No Prior Appointments
        </Typography>
      )}
    </Box>
  );
};

export default AppointmentPicker;
